// src/pages/MonProfil.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { http } from '../../../api/http';

/* ---------------- Modal 100% React (sans JS Bootstrap) ---------------- */
function Modal({ title, open, onClose, onSave, children, saving }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed-top w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ background: 'rgba(0,0,0,0.35)', zIndex: 1050 }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow"
        style={{ width: 'min(720px, 92vw)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
          <h5 className="mb-0">{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
        <div className="p-3">{children}</div>
        <div className="p-3 border-top d-flex justify-content-end">
          <button className="btn btn-light mr-2" onClick={onClose}>
            Annuler
          </button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------- Petit composant “label + valeur” --------------- */
function FieldRow({ label, value }) {
  return (
    <div className="mb-3">
      <div className="text-muted small">{label}</div>
      <div className="font-weight-medium">
        {value || <span className="text-muted">—</span>}
      </div>
    </div>
  );
}

/* ============================== PAGE ============================== */
export default function MonProfil() {
  const { user, loading, refresh } = useAuth();

  const [form, setForm] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    siret: '',
    email: '',
    phone: '',
    street: '',
    postalCode: '',
    city: '',
  });

  const [openPersonal, setOpenPersonal] = useState(false);
  const [openAddress, setOpenAddress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user) return;
    setForm({
      companyName: user.companyName || '',
      siret: user.siret || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      street: user.address?.street || '',
      postalCode: user.address?.postalCode || '',
      city: user.address?.city || '',
    });
  }, [user]);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const savePersonal = async () => {
    setSaving(true);
    setMessage('');
    try {
      await http.put('/me', {
        companyName: form.companyName,
        firstName: form.firstName,
        lastName: form.lastName,
        siret: form.siret,
        email: form.email,
        phone: form.phone,
      });
      await refresh();
      setOpenPersonal(false);
      setMessage('✅ Informations mises à jour.');
    } catch (e) {
      setMessage('❌ Erreur lors de la mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  const saveAddress = async () => {
    setSaving(true);
    setMessage('');
    try {
      await http.put('/me', {
        address: {
          street: form.street,
          postalCode: form.postalCode,
          city: form.city,
        },
      });

      await refresh();
      setOpenAddress(false);
      setMessage('✅ Adresse mise à jour.');
    } catch (e) {
      setMessage('❌ Erreur lors de la mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container mt-4">Chargement…</div>;
  if (!user) return <div className="container mt-4">Veuillez vous connecter.</div>;

  const card = 'bg-white rounded shadow-sm p-3';
  const sectionTitle = 'd-flex align-items-center justify-content-between mb-3';

  return (
    <div
      className="container my-4"
      style={{ maxWidth: 1100, marginTop: '4rem !important', paddingTop: '20px' }}
    >
      <h4 className="mb-3">Mon profil</h4>

      {/* Header */}
      <div className={`${card} mb-3`}>
        <div className="d-flex align-items-center">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              className="rounded-circle mr-3"
              style={{ width: 72, height: 72, objectFit: 'cover' }}
            />
          ) : (
            <div
              className="rounded-circle mr-3 d-flex align-items-center justify-content-center"
              style={{
                width: 72,
                height: 72,
                background: '#eef2f7',
                fontWeight: 700,
                color: '#1b3a8a',
              }}
            >
              {(user.companyName || user.email || 'U').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <div className="h5 mb-1">{user.companyName || 'Compte B2B'}</div>
            <div className="text-muted small">
              {(user.status || 'valid') + ' • ' + (user.email || '')}
            </div>
            {user.phone && <div className="text-muted small">{user.phone}</div>}
          </div>
        </div>
      </div>

      {/* Informations personnelles */}
      <div className={`${card} mb-3`}>
        <div className={sectionTitle}>
          <h6 className="mb-0">Informations personnelles</h6>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setOpenPersonal(true)}
          >
            Edit
          </button>
        </div>

        <div className="row">
          <div className="col-md-6 col-lg-4">
            <FieldRow label="Raison sociale" value={user.companyName} />
          </div>
          <div className="col-md-6 col-lg-4">
            <FieldRow label="Nom" value={user.lastName} />
          </div>
          <div className="col-md-6 col-lg-4">
            <FieldRow label="Prénom" value={user.firstName} />
          </div>
          <div className="col-md-6 col-lg-4">
            <FieldRow label="SIRET" value={user.siret} />
          </div>
          <div className="col-md-6 col-lg-4">
            <FieldRow label="Email" value={user.email} />
          </div>
          <div className="col-md-6 col-lg-4">
            <FieldRow label="Téléphone" value={user.phone} />
          </div>
          <div className="col-md-6 col-lg-4">
            <FieldRow label="Statut" value={user.status} />
          </div>
          <div className="col-md-6 col-lg-4">
            <FieldRow label="Rôle" value="B2B" />
          </div>
        </div>
      </div>

      {/* Adresse */}
      <div className={`${card} mb-3`}>
        <div className={sectionTitle}>
          <h6 className="mb-0">Adresse</h6>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setOpenAddress(true)}
          >
            Edit
          </button>
        </div>

        <div className="row">
          <div className="col-12">
            <FieldRow
              label="Adresse"
              value={[user.address?.street, user.address?.postalCode, user.address?.city]
                .filter(Boolean)
                .join(', ')}
            />
          </div>
        </div>
      </div>

      {message && <div className="alert alert-info">{message}</div>}

      {/* Modals */}
      <Modal
        title="Modifier mes informations"
        open={openPersonal}
        onClose={() => setOpenPersonal(false)}
        onSave={savePersonal}
        saving={saving}
      >
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Raison sociale</label>
            <input
              className="form-control"
              name="companyName"
              value={form.companyName}
              onChange={onChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>SIRET</label>
            <input
              className="form-control"
              name="siret"
              value={form.siret}
              onChange={onChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>Nom</label>
            <input
              className="form-control"
              name="firstName"
              value={form.firstName}
              onChange={onChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>Prénom</label>
            <input
              className="form-control"
              name="lastName"
              value={form.lastName}
              onChange={onChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>Email</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={form.email}
              onChange={onChange}
            />
          </div>
          <div className="form-group col-md-6">
            <label>Téléphone</label>
            <input
              className="form-control"
              name="phone"
              value={form.phone}
              onChange={onChange}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Modifier l’adresse"
        open={openAddress}
        onClose={() => setOpenAddress(false)}
        onSave={saveAddress}
        saving={saving}
      >
        <div className="form-group">
          <label>Adresse postale</label>
          <input
            className="form-control"
            name="street"
            value={form.street}
            onChange={onChange}
            placeholder="Ex : 23 rue ..."
          />
        </div>

        <div className="form-row">
          <div className="form-group col-md-4">
            <label>Code postal</label>
            <input
              className="form-control"
              name="postalCode"
              value={form.postalCode}
              onChange={onChange}
              placeholder="Ex : 75001"
            />
          </div>

          <div className="form-group col-md-8">
            <label>Ville</label>
            <input
              className="form-control"
              name="city"
              value={form.city}
              onChange={onChange}
              placeholder="Ex : Tunis"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
